import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { protectedResources } from '../../../authConfig';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import useFetchWithMsal from '../../useFetchWithMsal';

const useViewAttachments = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { instance } = useMsal();
  const [invoice, setInvoice] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [attachmentContent, setAttachmentContent] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [scale, setScale] = useState(1.0);
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [pdfError, setPdfError] = useState(false);
  
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  const calculateOptimalScale = useCallback((isMobile) => {
    const container = document.querySelector('.attachment-container');
    if (container) {
      const containerWidth = container.clientWidth - (isMobile ? 16 : 40);
      const pageWidth = 595;
      const maxScale = isMobile ? 1.2 : 1.5;
      return Math.min(containerWidth / pageWidth, maxScale);
    }
    return isMobile ? 0.8 : 1.0;
  }, []);

  // Fetch invoice info and attachments
  useEffect(() => {
    const fetchAttachments = async () => {
      try {
        setLoading(true);
        const response = await execute('GET', 
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/attachments/with-attachments/${invoiceId}`
        );
        
        if (response && response.success) {
          setInvoice(response.invoice);
          setAttachments(response.attachments);
          if (response.attachments.length > 0) {
            setSelectedAttachment(response.attachments[0]);
            await loadAttachmentContent(response.attachments[0]);
          }
        } else {
          setError('לא נמצאו צרופות');
        }
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          try {
            await instance.loginRedirect({
              scopes: protectedResources.apiScopes.scopes.read,
              redirectUri: window.location.href
            });
          } catch (authError) {
            setError('יש להתחבר למערכת כדי לצפות בצרופות');
          }
        } else {
          setError(err.message || 'שגיאה בטעינת הצרופות');
        }
      } finally {
        setLoading(false);
      }
    };

    if (invoiceId) {
      fetchAttachments();
    }
  }, [invoiceId, instance, execute]);

  const loadAttachmentContent = async (attachment) => {
    try {
      setAttachmentContent(null);
      setPdfError(false);
      
      console.log('Loading attachment:', attachment.name, attachment.originalFileName);
      const response = await execute('GET',
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/attachments/download-attachment/${invoiceId}/${attachment.name}`
      );
      
      if (response && response.success && response.fileContent) {
        console.log('Got base64 content, length:', response.fileContent.length);
        setAttachmentContent(response.fileContent);
      } else {
        throw new Error('Failed to load attachment content');
      }
      
      setPageNumber(1);
    } catch (error) {
      console.error('Error loading attachment:', error);
      setPdfError(true);
    }
  };

  const handleSelectAttachment = async (attachment) => {
    setSelectedAttachment(attachment);
    setAttachmentContent(null);
    await loadAttachmentContent(attachment);
  };

  const handleDownloadAttachment = async (attachment) => {
    if (!attachment) return;
    
    try {
      const response = await execute('GET',
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/attachments/download-attachment/${invoiceId}/${attachment.name}`
      );
      
      if (response && response.success && response.fileContent) {
        const byteCharacters = atob(response.fileContent);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: response.contentType || 'application/octet-stream' });
        
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.originalFileName || attachment.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
    }
  };

  const handleLogin = async () => {
    try {
      await instance.loginRedirect({
        scopes: protectedResources.apiScopes.scopes.read,
        redirectUri: window.location.href
      });
    } catch (error) {
      setError('שגיאה בתהליך ההתחברות');
    }
  };

  const handleBackToSystem = () => {
    navigate('/PurchaseInvoice');
  };

  const handleBackToInvoice = () => {
    navigate(`/v-i/${invoiceId}`);
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfError(false);
  };

  const onDocumentLoadError = () => {
    setPdfError(true);
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 3.0));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  };

  const toggleFullscreen = (isMobile) => {
    setIsFullscreen(prev => {
      if (!prev) {
        setScale(1.5);
      } else {
        setScale(calculateOptimalScale(isMobile));
      }
      return !prev;
    });
  };

  const getAttachmentSrc = () => {
    if (!attachmentContent) return null;
    if (attachmentContent.startsWith('data:application/pdf') || 
        attachmentContent.startsWith('http') || 
        attachmentContent.startsWith('blob:')) {
      return attachmentContent;
    }
    if (attachmentContent.match(/^[A-Za-z0-9+/]+={0,2}$/)) {
      return `data:application/pdf;base64,${attachmentContent}`;
    }
    
    return null; 
  };

  const getFileTypeIcon = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return '📄';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return '🖼️';
      case 'doc':
      case 'docx':
        return '📝';
      case 'xls':
      case 'xlsx':
        return '📊';
      default:
        return '📎';
    }
  };

  const isPdfFile = (fileName) => {
    return fileName.toLowerCase().endsWith('.pdf');
  };

  return {
    invoice,
    attachments,
    selectedAttachment,
    attachmentContent,
    loading,
    error,
    isFullscreen,
    scale,
    pageNumber,
    numPages,
    pdfError,
    setPageNumber,
    setScale,
    setIsFullscreen,
    handleSelectAttachment,
    handleDownloadAttachment,
    handleLogin,
    handleBackToSystem,
    handleBackToInvoice,
    handleZoomIn,
    handleZoomOut,
    toggleFullscreen,
    onDocumentLoadSuccess,
    onDocumentLoadError,
    getAttachmentSrc,
    getFileTypeIcon,
    isPdfFile,
    calculateOptimalScale,
  };
};

export default useViewAttachments;